import * as z from 'zod';

export const GetReferralProgramRequest = z.object({});
export const GetReferralProgramResponse = z.object({
  referralProgram: z
    .object({
      name: z
        .string()
        .describe('Referral program name.')
        .min(2)
        .max(50)
        .optional()
        .nullable(),
      status: z.enum(['UNKNOWN', 'DRAFT', 'ACTIVE', 'PAUSED']).optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the program is updated.\nTo prevent conflicting changes, the current `revision` must be passed when updating the program.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the program was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the program was last updated.')
        .optional()
        .nullable(),
      referredFriendReward: z
        .intersection(
          z.object({
            type: z
              .enum(['UNKNOWN', 'COUPON', 'LOYALTY_POINTS', 'NOTHING'])
              .describe('Type of the reward.')
              .optional(),
          }),
          z.xor([
            z.object({
              couponOptions: z.never().optional(),
              loyaltyPointsOptions: z.never().optional(),
            }),
            z.object({
              loyaltyPointsOptions: z.never().optional(),
              couponOptions: z
                .intersection(
                  z.object({
                    name: z
                      .string()
                      .describe('Coupon name.')
                      .min(1)
                      .max(50)
                      .optional(),
                    discountType: z
                      .enum([
                        'UNKNOWN',
                        'FIXED_AMOUNT',
                        'PERCENTAGE',
                        'FREE_SHIPPING',
                      ])
                      .describe('Coupon discount type.')
                      .optional(),
                    limitedToOneItem: z
                      .boolean()
                      .describe(
                        'Whether the coupon is limited to one item.\nIf `true` and a customer pays for multiple items, the discount applies to only the lowest priced item.\nCoupons with a bookings `scope.namespace` are always limited to one item.'
                      )
                      .optional()
                      .nullable(),
                    appliesToSubscriptions: z
                      .boolean()
                      .describe(
                        'Whether the coupon applies to subscription products.'
                      )
                      .optional()
                      .nullable(),
                    discountedCycleCount: z
                      .number()
                      .int()
                      .describe(
                        'Specifies the amount of discounted cycles for a subscription item.\n\n- Can only be set when `scope.namespace = pricingPlans`.\n- If `discountedCycleCount` is empty, the coupon applies to all available cycles.\n- `discountedCycleCount` is ignored if `appliesToSubscriptions = true`.\n\nMax: `999`'
                      )
                      .optional()
                      .nullable(),
                  }),
                  z.intersection(
                    z.xor([
                      z.object({
                        fixedAmountOptions: z.never().optional(),
                        percentageOptions: z.never().optional(),
                      }),
                      z.object({
                        percentageOptions: z.never().optional(),
                        fixedAmountOptions: z
                          .object({
                            amount: z
                              .number()
                              .describe(
                                'Amount of the discount as a fixed value.'
                              )
                              .min(0.01)
                              .optional(),
                          })
                          .describe('Options for fixed amount discount.'),
                      }),
                      z.object({
                        fixedAmountOptions: z.never().optional(),
                        percentageOptions: z
                          .object({
                            percentage: z
                              .number()
                              .describe('Percentage of discount.')
                              .min(0)
                              .max(100)
                              .optional(),
                          })
                          .describe('Options for percentage discounts.'),
                      }),
                    ]),
                    z.xor([
                      z.object({
                        minimumSubtotal: z.never().optional(),
                        scope: z.never().optional(),
                      }),
                      z.object({
                        scope: z.never().optional(),
                        minimumSubtotal: z
                          .number()
                          .describe(
                            'Limit the coupon to carts with a subtotal above this number.'
                          ),
                      }),
                      z.object({
                        minimumSubtotal: z.never().optional(),
                        scope: z
                          .object({
                            namespace: z
                              .string()
                              .describe(
                                'Scope namespace (Wix Stores, Wix Bookings, Wix Events, Wix Pricing Plans)'
                              )
                              .optional(),
                            group: z
                              .object({
                                name: z
                                  .string()
                                  .describe('Name of the group.')
                                  .optional(),
                                entityId: z
                                  .string()
                                  .describe('Entity ID of the group.')
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                "Coupon scope's applied group, for example, Event or ticket in Wix Events."
                              )
                              .optional(),
                          })
                          .describe(
                            'Specifies the type of line items this coupon will apply to. See [valid scope values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values).'
                          ),
                      }),
                    ])
                  )
                )
                .describe('Options for coupon reward type.'),
            }),
            z.object({
              couponOptions: z.never().optional(),
              loyaltyPointsOptions: z
                .object({
                  amount: z
                    .number()
                    .int()
                    .describe('Number of loyalty points to give.')
                    .min(1)
                    .max(9999999)
                    .optional(),
                })
                .describe('Options for the Loyalty points reward type.'),
            }),
          ])
        )
        .describe(
          'Reward configuration for the referred friend.\nSpecifies the reward given to a new customer who was referred to the business.'
        )
        .optional(),
      referringCustomerReward: z
        .intersection(
          z.object({
            type: z
              .enum(['UNKNOWN', 'COUPON', 'LOYALTY_POINTS', 'NOTHING'])
              .describe('Type of the reward.')
              .optional(),
          }),
          z.xor([
            z.object({
              couponOptions: z.never().optional(),
              loyaltyPointsOptions: z.never().optional(),
            }),
            z.object({
              loyaltyPointsOptions: z.never().optional(),
              couponOptions: z
                .intersection(
                  z.object({
                    name: z
                      .string()
                      .describe('Coupon name.')
                      .min(1)
                      .max(50)
                      .optional(),
                    discountType: z
                      .enum([
                        'UNKNOWN',
                        'FIXED_AMOUNT',
                        'PERCENTAGE',
                        'FREE_SHIPPING',
                      ])
                      .describe('Coupon discount type.')
                      .optional(),
                    limitedToOneItem: z
                      .boolean()
                      .describe(
                        'Whether the coupon is limited to one item.\nIf `true` and a customer pays for multiple items, the discount applies to only the lowest priced item.\nCoupons with a bookings `scope.namespace` are always limited to one item.'
                      )
                      .optional()
                      .nullable(),
                    appliesToSubscriptions: z
                      .boolean()
                      .describe(
                        'Whether the coupon applies to subscription products.'
                      )
                      .optional()
                      .nullable(),
                    discountedCycleCount: z
                      .number()
                      .int()
                      .describe(
                        'Specifies the amount of discounted cycles for a subscription item.\n\n- Can only be set when `scope.namespace = pricingPlans`.\n- If `discountedCycleCount` is empty, the coupon applies to all available cycles.\n- `discountedCycleCount` is ignored if `appliesToSubscriptions = true`.\n\nMax: `999`'
                      )
                      .optional()
                      .nullable(),
                  }),
                  z.intersection(
                    z.xor([
                      z.object({
                        fixedAmountOptions: z.never().optional(),
                        percentageOptions: z.never().optional(),
                      }),
                      z.object({
                        percentageOptions: z.never().optional(),
                        fixedAmountOptions: z
                          .object({
                            amount: z
                              .number()
                              .describe(
                                'Amount of the discount as a fixed value.'
                              )
                              .min(0.01)
                              .optional(),
                          })
                          .describe('Options for fixed amount discount.'),
                      }),
                      z.object({
                        fixedAmountOptions: z.never().optional(),
                        percentageOptions: z
                          .object({
                            percentage: z
                              .number()
                              .describe('Percentage of discount.')
                              .min(0)
                              .max(100)
                              .optional(),
                          })
                          .describe('Options for percentage discounts.'),
                      }),
                    ]),
                    z.xor([
                      z.object({
                        minimumSubtotal: z.never().optional(),
                        scope: z.never().optional(),
                      }),
                      z.object({
                        scope: z.never().optional(),
                        minimumSubtotal: z
                          .number()
                          .describe(
                            'Limit the coupon to carts with a subtotal above this number.'
                          ),
                      }),
                      z.object({
                        minimumSubtotal: z.never().optional(),
                        scope: z
                          .object({
                            namespace: z
                              .string()
                              .describe(
                                'Scope namespace (Wix Stores, Wix Bookings, Wix Events, Wix Pricing Plans)'
                              )
                              .optional(),
                            group: z
                              .object({
                                name: z
                                  .string()
                                  .describe('Name of the group.')
                                  .optional(),
                                entityId: z
                                  .string()
                                  .describe('Entity ID of the group.')
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                "Coupon scope's applied group, for example, Event or ticket in Wix Events."
                              )
                              .optional(),
                          })
                          .describe(
                            'Specifies the type of line items this coupon will apply to. See [valid scope values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values).'
                          ),
                      }),
                    ])
                  )
                )
                .describe('Options for coupon reward type.'),
            }),
            z.object({
              couponOptions: z.never().optional(),
              loyaltyPointsOptions: z
                .object({
                  amount: z
                    .number()
                    .int()
                    .describe('Number of loyalty points to give.')
                    .min(1)
                    .max(9999999)
                    .optional(),
                })
                .describe('Options for the Loyalty points reward type.'),
            }),
          ])
        )
        .describe(
          'Reward configuration for the referring customer.\nSpecifies the reward given to an existing customer who referred a new customer to the business.'
        )
        .optional(),
      successfulReferralActions: z
        .array(
          z.enum([
            'UNKNOWN',
            'STORE_ORDER_PLACED',
            'PLAN_ORDERED',
            'TICKET_ORDERED',
            'SESSION_BOOKED',
            'RESTAURANT_ORDER_PLACED',
            'ONLINE_PROGRAM_JOINED',
          ])
        )
        .max(100)
        .optional(),
      emails: z
        .object({
          encourageToReferFriends: z
            .array(
              z.enum([
                'UNKNOWN',
                'STORES',
                'PRICING_PLANS',
                'EVENTS',
                'BOOKINGS',
                'RESTAURANTS',
                'ONLINE_PROGRAMS',
              ])
            )
            .optional(),
          notifyCustomersAboutReward: z
            .boolean()
            .describe(
              'Whether to send email notifications to referring customers when they receive a referral reward.\nIf true, referring customers will be notified by email when their referred friend completes a qualifying action (for example, placing an order).'
            )
            .optional(),
        })
        .describe('Configures email notifications for the referral program.')
        .optional(),
      premiumFeatures: z
        .object({
          referralProgram: z
            .boolean()
            .describe(
              'Whether the site owner has access to the referral program feature.'
            )
            .optional(),
        })
        .describe(
          'Indicates which premium features are available for the current account.'
        )
        .optional(),
    })
    .describe('Retrieved referral program.')
    .optional(),
});
export const UpdateReferralProgramRequest = z.object({
  referralProgram: z
    .object({
      name: z
        .string()
        .describe('Referral program name.')
        .min(2)
        .max(50)
        .optional()
        .nullable(),
      status: z.enum(['UNKNOWN', 'DRAFT', 'ACTIVE', 'PAUSED']).optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the program is updated.\nTo prevent conflicting changes, the current `revision` must be passed when updating the program.'
        ),
      _createdDate: z
        .date()
        .describe('Date and time the program was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the program was last updated.')
        .optional()
        .nullable(),
      referredFriendReward: z
        .intersection(
          z.object({
            type: z
              .enum(['UNKNOWN', 'COUPON', 'LOYALTY_POINTS', 'NOTHING'])
              .optional(),
          }),
          z.xor([
            z.object({
              couponOptions: z.never().optional(),
              loyaltyPointsOptions: z.never().optional(),
            }),
            z.object({
              loyaltyPointsOptions: z.never().optional(),
              couponOptions: z
                .intersection(
                  z.object({
                    name: z
                      .string()
                      .describe('Coupon name.')
                      .min(1)
                      .max(50)
                      .optional(),
                    discountType: z
                      .enum([
                        'UNKNOWN',
                        'FIXED_AMOUNT',
                        'PERCENTAGE',
                        'FREE_SHIPPING',
                      ])
                      .describe('Coupon discount type.')
                      .optional(),
                    limitedToOneItem: z
                      .boolean()
                      .describe(
                        'Whether the coupon is limited to one item.\nIf `true` and a customer pays for multiple items, the discount applies to only the lowest priced item.\nCoupons with a bookings `scope.namespace` are always limited to one item.'
                      )
                      .optional()
                      .nullable(),
                    appliesToSubscriptions: z
                      .boolean()
                      .describe(
                        'Whether the coupon applies to subscription products.'
                      )
                      .optional()
                      .nullable(),
                    discountedCycleCount: z
                      .number()
                      .int()
                      .describe(
                        'Specifies the amount of discounted cycles for a subscription item.\n\n- Can only be set when `scope.namespace = pricingPlans`.\n- If `discountedCycleCount` is empty, the coupon applies to all available cycles.\n- `discountedCycleCount` is ignored if `appliesToSubscriptions = true`.\n\nMax: `999`'
                      )
                      .optional()
                      .nullable(),
                  }),
                  z.intersection(
                    z.xor([
                      z.object({
                        fixedAmountOptions: z.never().optional(),
                        percentageOptions: z.never().optional(),
                      }),
                      z.object({
                        percentageOptions: z.never().optional(),
                        fixedAmountOptions: z
                          .object({
                            amount: z
                              .number()
                              .describe(
                                'Amount of the discount as a fixed value.'
                              )
                              .min(0.01)
                              .optional(),
                          })
                          .describe('Options for fixed amount discount.'),
                      }),
                      z.object({
                        fixedAmountOptions: z.never().optional(),
                        percentageOptions: z
                          .object({
                            percentage: z
                              .number()
                              .describe('Percentage of discount.')
                              .min(0)
                              .max(100)
                              .optional(),
                          })
                          .describe('Options for percentage discounts.'),
                      }),
                    ]),
                    z.xor([
                      z.object({
                        minimumSubtotal: z.never().optional(),
                        scope: z.never().optional(),
                      }),
                      z.object({
                        scope: z.never().optional(),
                        minimumSubtotal: z
                          .number()
                          .describe(
                            'Limit the coupon to carts with a subtotal above this number.'
                          ),
                      }),
                      z.object({
                        minimumSubtotal: z.never().optional(),
                        scope: z
                          .object({
                            namespace: z
                              .string()
                              .describe(
                                'Scope namespace (Wix Stores, Wix Bookings, Wix Events, Wix Pricing Plans)'
                              )
                              .optional(),
                            group: z
                              .object({
                                name: z
                                  .string()
                                  .describe('Name of the group.')
                                  .optional(),
                                entityId: z
                                  .string()
                                  .describe('Entity ID of the group.')
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                "Coupon scope's applied group, for example, Event or ticket in Wix Events."
                              )
                              .optional(),
                          })
                          .describe(
                            'Specifies the type of line items this coupon will apply to. See [valid scope values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values).'
                          ),
                      }),
                    ])
                  )
                )
                .describe('Options for coupon reward type.'),
            }),
            z.object({
              couponOptions: z.never().optional(),
              loyaltyPointsOptions: z
                .object({
                  amount: z
                    .number()
                    .int()
                    .describe('Number of loyalty points to give.')
                    .min(1)
                    .max(9999999)
                    .optional(),
                })
                .describe('Options for the Loyalty points reward type.'),
            }),
          ])
        )
        .describe(
          'Reward configuration for the referred friend.\nSpecifies the reward given to a new customer who was referred to the business.'
        )
        .optional(),
      referringCustomerReward: z
        .intersection(
          z.object({
            type: z
              .enum(['UNKNOWN', 'COUPON', 'LOYALTY_POINTS', 'NOTHING'])
              .optional(),
          }),
          z.xor([
            z.object({
              couponOptions: z.never().optional(),
              loyaltyPointsOptions: z.never().optional(),
            }),
            z.object({
              loyaltyPointsOptions: z.never().optional(),
              couponOptions: z
                .intersection(
                  z.object({
                    name: z
                      .string()
                      .describe('Coupon name.')
                      .min(1)
                      .max(50)
                      .optional(),
                    discountType: z
                      .enum([
                        'UNKNOWN',
                        'FIXED_AMOUNT',
                        'PERCENTAGE',
                        'FREE_SHIPPING',
                      ])
                      .describe('Coupon discount type.')
                      .optional(),
                    limitedToOneItem: z
                      .boolean()
                      .describe(
                        'Whether the coupon is limited to one item.\nIf `true` and a customer pays for multiple items, the discount applies to only the lowest priced item.\nCoupons with a bookings `scope.namespace` are always limited to one item.'
                      )
                      .optional()
                      .nullable(),
                    appliesToSubscriptions: z
                      .boolean()
                      .describe(
                        'Whether the coupon applies to subscription products.'
                      )
                      .optional()
                      .nullable(),
                    discountedCycleCount: z
                      .number()
                      .int()
                      .describe(
                        'Specifies the amount of discounted cycles for a subscription item.\n\n- Can only be set when `scope.namespace = pricingPlans`.\n- If `discountedCycleCount` is empty, the coupon applies to all available cycles.\n- `discountedCycleCount` is ignored if `appliesToSubscriptions = true`.\n\nMax: `999`'
                      )
                      .optional()
                      .nullable(),
                  }),
                  z.intersection(
                    z.xor([
                      z.object({
                        fixedAmountOptions: z.never().optional(),
                        percentageOptions: z.never().optional(),
                      }),
                      z.object({
                        percentageOptions: z.never().optional(),
                        fixedAmountOptions: z
                          .object({
                            amount: z
                              .number()
                              .describe(
                                'Amount of the discount as a fixed value.'
                              )
                              .min(0.01)
                              .optional(),
                          })
                          .describe('Options for fixed amount discount.'),
                      }),
                      z.object({
                        fixedAmountOptions: z.never().optional(),
                        percentageOptions: z
                          .object({
                            percentage: z
                              .number()
                              .describe('Percentage of discount.')
                              .min(0)
                              .max(100)
                              .optional(),
                          })
                          .describe('Options for percentage discounts.'),
                      }),
                    ]),
                    z.xor([
                      z.object({
                        minimumSubtotal: z.never().optional(),
                        scope: z.never().optional(),
                      }),
                      z.object({
                        scope: z.never().optional(),
                        minimumSubtotal: z
                          .number()
                          .describe(
                            'Limit the coupon to carts with a subtotal above this number.'
                          ),
                      }),
                      z.object({
                        minimumSubtotal: z.never().optional(),
                        scope: z
                          .object({
                            namespace: z
                              .string()
                              .describe(
                                'Scope namespace (Wix Stores, Wix Bookings, Wix Events, Wix Pricing Plans)'
                              )
                              .optional(),
                            group: z
                              .object({
                                name: z
                                  .string()
                                  .describe('Name of the group.')
                                  .optional(),
                                entityId: z
                                  .string()
                                  .describe('Entity ID of the group.')
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                "Coupon scope's applied group, for example, Event or ticket in Wix Events."
                              )
                              .optional(),
                          })
                          .describe(
                            'Specifies the type of line items this coupon will apply to. See [valid scope values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values).'
                          ),
                      }),
                    ])
                  )
                )
                .describe('Options for coupon reward type.'),
            }),
            z.object({
              couponOptions: z.never().optional(),
              loyaltyPointsOptions: z
                .object({
                  amount: z
                    .number()
                    .int()
                    .describe('Number of loyalty points to give.')
                    .min(1)
                    .max(9999999)
                    .optional(),
                })
                .describe('Options for the Loyalty points reward type.'),
            }),
          ])
        )
        .describe(
          'Reward configuration for the referring customer.\nSpecifies the reward given to an existing customer who referred a new customer to the business.'
        )
        .optional(),
      successfulReferralActions: z
        .array(
          z.enum([
            'UNKNOWN',
            'STORE_ORDER_PLACED',
            'PLAN_ORDERED',
            'TICKET_ORDERED',
            'SESSION_BOOKED',
            'RESTAURANT_ORDER_PLACED',
            'ONLINE_PROGRAM_JOINED',
          ])
        )
        .max(100)
        .optional(),
      emails: z
        .object({
          encourageToReferFriends: z
            .array(
              z.enum([
                'UNKNOWN',
                'STORES',
                'PRICING_PLANS',
                'EVENTS',
                'BOOKINGS',
                'RESTAURANTS',
                'ONLINE_PROGRAMS',
              ])
            )
            .optional(),
          notifyCustomersAboutReward: z
            .boolean()
            .describe(
              'Whether to send email notifications to referring customers when they receive a referral reward.\nIf true, referring customers will be notified by email when their referred friend completes a qualifying action (for example, placing an order).'
            )
            .optional(),
        })
        .describe('Configures email notifications for the referral program.')
        .optional(),
      premiumFeatures: z
        .object({
          referralProgram: z
            .boolean()
            .describe(
              'Whether the site owner has access to the referral program feature.'
            )
            .optional(),
        })
        .describe(
          'Indicates which premium features are available for the current account.'
        )
        .optional(),
    })
    .describe(
      'Referral program to update. Include the latest `revision` for a successful update.'
    ),
});
export const UpdateReferralProgramResponse = z.object({
  referralProgram: z
    .object({
      name: z
        .string()
        .describe('Referral program name.')
        .min(2)
        .max(50)
        .optional()
        .nullable(),
      status: z.enum(['UNKNOWN', 'DRAFT', 'ACTIVE', 'PAUSED']).optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the program is updated.\nTo prevent conflicting changes, the current `revision` must be passed when updating the program.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the program was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the program was last updated.')
        .optional()
        .nullable(),
      referredFriendReward: z
        .intersection(
          z.object({
            type: z
              .enum(['UNKNOWN', 'COUPON', 'LOYALTY_POINTS', 'NOTHING'])
              .describe('Type of the reward.')
              .optional(),
          }),
          z.xor([
            z.object({
              couponOptions: z.never().optional(),
              loyaltyPointsOptions: z.never().optional(),
            }),
            z.object({
              loyaltyPointsOptions: z.never().optional(),
              couponOptions: z
                .intersection(
                  z.object({
                    name: z
                      .string()
                      .describe('Coupon name.')
                      .min(1)
                      .max(50)
                      .optional(),
                    discountType: z
                      .enum([
                        'UNKNOWN',
                        'FIXED_AMOUNT',
                        'PERCENTAGE',
                        'FREE_SHIPPING',
                      ])
                      .describe('Coupon discount type.')
                      .optional(),
                    limitedToOneItem: z
                      .boolean()
                      .describe(
                        'Whether the coupon is limited to one item.\nIf `true` and a customer pays for multiple items, the discount applies to only the lowest priced item.\nCoupons with a bookings `scope.namespace` are always limited to one item.'
                      )
                      .optional()
                      .nullable(),
                    appliesToSubscriptions: z
                      .boolean()
                      .describe(
                        'Whether the coupon applies to subscription products.'
                      )
                      .optional()
                      .nullable(),
                    discountedCycleCount: z
                      .number()
                      .int()
                      .describe(
                        'Specifies the amount of discounted cycles for a subscription item.\n\n- Can only be set when `scope.namespace = pricingPlans`.\n- If `discountedCycleCount` is empty, the coupon applies to all available cycles.\n- `discountedCycleCount` is ignored if `appliesToSubscriptions = true`.\n\nMax: `999`'
                      )
                      .optional()
                      .nullable(),
                  }),
                  z.intersection(
                    z.xor([
                      z.object({
                        fixedAmountOptions: z.never().optional(),
                        percentageOptions: z.never().optional(),
                      }),
                      z.object({
                        percentageOptions: z.never().optional(),
                        fixedAmountOptions: z
                          .object({
                            amount: z
                              .number()
                              .describe(
                                'Amount of the discount as a fixed value.'
                              )
                              .min(0.01)
                              .optional(),
                          })
                          .describe('Options for fixed amount discount.'),
                      }),
                      z.object({
                        fixedAmountOptions: z.never().optional(),
                        percentageOptions: z
                          .object({
                            percentage: z
                              .number()
                              .describe('Percentage of discount.')
                              .min(0)
                              .max(100)
                              .optional(),
                          })
                          .describe('Options for percentage discounts.'),
                      }),
                    ]),
                    z.xor([
                      z.object({
                        minimumSubtotal: z.never().optional(),
                        scope: z.never().optional(),
                      }),
                      z.object({
                        scope: z.never().optional(),
                        minimumSubtotal: z
                          .number()
                          .describe(
                            'Limit the coupon to carts with a subtotal above this number.'
                          ),
                      }),
                      z.object({
                        minimumSubtotal: z.never().optional(),
                        scope: z
                          .object({
                            namespace: z
                              .string()
                              .describe(
                                'Scope namespace (Wix Stores, Wix Bookings, Wix Events, Wix Pricing Plans)'
                              )
                              .optional(),
                            group: z
                              .object({
                                name: z
                                  .string()
                                  .describe('Name of the group.')
                                  .optional(),
                                entityId: z
                                  .string()
                                  .describe('Entity ID of the group.')
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                "Coupon scope's applied group, for example, Event or ticket in Wix Events."
                              )
                              .optional(),
                          })
                          .describe(
                            'Specifies the type of line items this coupon will apply to. See [valid scope values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values).'
                          ),
                      }),
                    ])
                  )
                )
                .describe('Options for coupon reward type.'),
            }),
            z.object({
              couponOptions: z.never().optional(),
              loyaltyPointsOptions: z
                .object({
                  amount: z
                    .number()
                    .int()
                    .describe('Number of loyalty points to give.')
                    .min(1)
                    .max(9999999)
                    .optional(),
                })
                .describe('Options for the Loyalty points reward type.'),
            }),
          ])
        )
        .describe(
          'Reward configuration for the referred friend.\nSpecifies the reward given to a new customer who was referred to the business.'
        )
        .optional(),
      referringCustomerReward: z
        .intersection(
          z.object({
            type: z
              .enum(['UNKNOWN', 'COUPON', 'LOYALTY_POINTS', 'NOTHING'])
              .describe('Type of the reward.')
              .optional(),
          }),
          z.xor([
            z.object({
              couponOptions: z.never().optional(),
              loyaltyPointsOptions: z.never().optional(),
            }),
            z.object({
              loyaltyPointsOptions: z.never().optional(),
              couponOptions: z
                .intersection(
                  z.object({
                    name: z
                      .string()
                      .describe('Coupon name.')
                      .min(1)
                      .max(50)
                      .optional(),
                    discountType: z
                      .enum([
                        'UNKNOWN',
                        'FIXED_AMOUNT',
                        'PERCENTAGE',
                        'FREE_SHIPPING',
                      ])
                      .describe('Coupon discount type.')
                      .optional(),
                    limitedToOneItem: z
                      .boolean()
                      .describe(
                        'Whether the coupon is limited to one item.\nIf `true` and a customer pays for multiple items, the discount applies to only the lowest priced item.\nCoupons with a bookings `scope.namespace` are always limited to one item.'
                      )
                      .optional()
                      .nullable(),
                    appliesToSubscriptions: z
                      .boolean()
                      .describe(
                        'Whether the coupon applies to subscription products.'
                      )
                      .optional()
                      .nullable(),
                    discountedCycleCount: z
                      .number()
                      .int()
                      .describe(
                        'Specifies the amount of discounted cycles for a subscription item.\n\n- Can only be set when `scope.namespace = pricingPlans`.\n- If `discountedCycleCount` is empty, the coupon applies to all available cycles.\n- `discountedCycleCount` is ignored if `appliesToSubscriptions = true`.\n\nMax: `999`'
                      )
                      .optional()
                      .nullable(),
                  }),
                  z.intersection(
                    z.xor([
                      z.object({
                        fixedAmountOptions: z.never().optional(),
                        percentageOptions: z.never().optional(),
                      }),
                      z.object({
                        percentageOptions: z.never().optional(),
                        fixedAmountOptions: z
                          .object({
                            amount: z
                              .number()
                              .describe(
                                'Amount of the discount as a fixed value.'
                              )
                              .min(0.01)
                              .optional(),
                          })
                          .describe('Options for fixed amount discount.'),
                      }),
                      z.object({
                        fixedAmountOptions: z.never().optional(),
                        percentageOptions: z
                          .object({
                            percentage: z
                              .number()
                              .describe('Percentage of discount.')
                              .min(0)
                              .max(100)
                              .optional(),
                          })
                          .describe('Options for percentage discounts.'),
                      }),
                    ]),
                    z.xor([
                      z.object({
                        minimumSubtotal: z.never().optional(),
                        scope: z.never().optional(),
                      }),
                      z.object({
                        scope: z.never().optional(),
                        minimumSubtotal: z
                          .number()
                          .describe(
                            'Limit the coupon to carts with a subtotal above this number.'
                          ),
                      }),
                      z.object({
                        minimumSubtotal: z.never().optional(),
                        scope: z
                          .object({
                            namespace: z
                              .string()
                              .describe(
                                'Scope namespace (Wix Stores, Wix Bookings, Wix Events, Wix Pricing Plans)'
                              )
                              .optional(),
                            group: z
                              .object({
                                name: z
                                  .string()
                                  .describe('Name of the group.')
                                  .optional(),
                                entityId: z
                                  .string()
                                  .describe('Entity ID of the group.')
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                "Coupon scope's applied group, for example, Event or ticket in Wix Events."
                              )
                              .optional(),
                          })
                          .describe(
                            'Specifies the type of line items this coupon will apply to. See [valid scope values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values).'
                          ),
                      }),
                    ])
                  )
                )
                .describe('Options for coupon reward type.'),
            }),
            z.object({
              couponOptions: z.never().optional(),
              loyaltyPointsOptions: z
                .object({
                  amount: z
                    .number()
                    .int()
                    .describe('Number of loyalty points to give.')
                    .min(1)
                    .max(9999999)
                    .optional(),
                })
                .describe('Options for the Loyalty points reward type.'),
            }),
          ])
        )
        .describe(
          'Reward configuration for the referring customer.\nSpecifies the reward given to an existing customer who referred a new customer to the business.'
        )
        .optional(),
      successfulReferralActions: z
        .array(
          z.enum([
            'UNKNOWN',
            'STORE_ORDER_PLACED',
            'PLAN_ORDERED',
            'TICKET_ORDERED',
            'SESSION_BOOKED',
            'RESTAURANT_ORDER_PLACED',
            'ONLINE_PROGRAM_JOINED',
          ])
        )
        .max(100)
        .optional(),
      emails: z
        .object({
          encourageToReferFriends: z
            .array(
              z.enum([
                'UNKNOWN',
                'STORES',
                'PRICING_PLANS',
                'EVENTS',
                'BOOKINGS',
                'RESTAURANTS',
                'ONLINE_PROGRAMS',
              ])
            )
            .optional(),
          notifyCustomersAboutReward: z
            .boolean()
            .describe(
              'Whether to send email notifications to referring customers when they receive a referral reward.\nIf true, referring customers will be notified by email when their referred friend completes a qualifying action (for example, placing an order).'
            )
            .optional(),
        })
        .describe('Configures email notifications for the referral program.')
        .optional(),
      premiumFeatures: z
        .object({
          referralProgram: z
            .boolean()
            .describe(
              'Whether the site owner has access to the referral program feature.'
            )
            .optional(),
        })
        .describe(
          'Indicates which premium features are available for the current account.'
        )
        .optional(),
    })
    .describe('Updated referral program.')
    .optional(),
});
export const ActivateReferralProgramRequest = z.object({});
export const ActivateReferralProgramResponse = z.object({
  referralProgram: z
    .object({
      name: z
        .string()
        .describe('Referral program name.')
        .min(2)
        .max(50)
        .optional()
        .nullable(),
      status: z.enum(['UNKNOWN', 'DRAFT', 'ACTIVE', 'PAUSED']).optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the program is updated.\nTo prevent conflicting changes, the current `revision` must be passed when updating the program.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the program was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the program was last updated.')
        .optional()
        .nullable(),
      referredFriendReward: z
        .intersection(
          z.object({
            type: z
              .enum(['UNKNOWN', 'COUPON', 'LOYALTY_POINTS', 'NOTHING'])
              .describe('Type of the reward.')
              .optional(),
          }),
          z.xor([
            z.object({
              couponOptions: z.never().optional(),
              loyaltyPointsOptions: z.never().optional(),
            }),
            z.object({
              loyaltyPointsOptions: z.never().optional(),
              couponOptions: z
                .intersection(
                  z.object({
                    name: z
                      .string()
                      .describe('Coupon name.')
                      .min(1)
                      .max(50)
                      .optional(),
                    discountType: z
                      .enum([
                        'UNKNOWN',
                        'FIXED_AMOUNT',
                        'PERCENTAGE',
                        'FREE_SHIPPING',
                      ])
                      .describe('Coupon discount type.')
                      .optional(),
                    limitedToOneItem: z
                      .boolean()
                      .describe(
                        'Whether the coupon is limited to one item.\nIf `true` and a customer pays for multiple items, the discount applies to only the lowest priced item.\nCoupons with a bookings `scope.namespace` are always limited to one item.'
                      )
                      .optional()
                      .nullable(),
                    appliesToSubscriptions: z
                      .boolean()
                      .describe(
                        'Whether the coupon applies to subscription products.'
                      )
                      .optional()
                      .nullable(),
                    discountedCycleCount: z
                      .number()
                      .int()
                      .describe(
                        'Specifies the amount of discounted cycles for a subscription item.\n\n- Can only be set when `scope.namespace = pricingPlans`.\n- If `discountedCycleCount` is empty, the coupon applies to all available cycles.\n- `discountedCycleCount` is ignored if `appliesToSubscriptions = true`.\n\nMax: `999`'
                      )
                      .optional()
                      .nullable(),
                  }),
                  z.intersection(
                    z.xor([
                      z.object({
                        fixedAmountOptions: z.never().optional(),
                        percentageOptions: z.never().optional(),
                      }),
                      z.object({
                        percentageOptions: z.never().optional(),
                        fixedAmountOptions: z
                          .object({
                            amount: z
                              .number()
                              .describe(
                                'Amount of the discount as a fixed value.'
                              )
                              .min(0.01)
                              .optional(),
                          })
                          .describe('Options for fixed amount discount.'),
                      }),
                      z.object({
                        fixedAmountOptions: z.never().optional(),
                        percentageOptions: z
                          .object({
                            percentage: z
                              .number()
                              .describe('Percentage of discount.')
                              .min(0)
                              .max(100)
                              .optional(),
                          })
                          .describe('Options for percentage discounts.'),
                      }),
                    ]),
                    z.xor([
                      z.object({
                        minimumSubtotal: z.never().optional(),
                        scope: z.never().optional(),
                      }),
                      z.object({
                        scope: z.never().optional(),
                        minimumSubtotal: z
                          .number()
                          .describe(
                            'Limit the coupon to carts with a subtotal above this number.'
                          ),
                      }),
                      z.object({
                        minimumSubtotal: z.never().optional(),
                        scope: z
                          .object({
                            namespace: z
                              .string()
                              .describe(
                                'Scope namespace (Wix Stores, Wix Bookings, Wix Events, Wix Pricing Plans)'
                              )
                              .optional(),
                            group: z
                              .object({
                                name: z
                                  .string()
                                  .describe('Name of the group.')
                                  .optional(),
                                entityId: z
                                  .string()
                                  .describe('Entity ID of the group.')
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                "Coupon scope's applied group, for example, Event or ticket in Wix Events."
                              )
                              .optional(),
                          })
                          .describe(
                            'Specifies the type of line items this coupon will apply to. See [valid scope values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values).'
                          ),
                      }),
                    ])
                  )
                )
                .describe('Options for coupon reward type.'),
            }),
            z.object({
              couponOptions: z.never().optional(),
              loyaltyPointsOptions: z
                .object({
                  amount: z
                    .number()
                    .int()
                    .describe('Number of loyalty points to give.')
                    .min(1)
                    .max(9999999)
                    .optional(),
                })
                .describe('Options for the Loyalty points reward type.'),
            }),
          ])
        )
        .describe(
          'Reward configuration for the referred friend.\nSpecifies the reward given to a new customer who was referred to the business.'
        )
        .optional(),
      referringCustomerReward: z
        .intersection(
          z.object({
            type: z
              .enum(['UNKNOWN', 'COUPON', 'LOYALTY_POINTS', 'NOTHING'])
              .describe('Type of the reward.')
              .optional(),
          }),
          z.xor([
            z.object({
              couponOptions: z.never().optional(),
              loyaltyPointsOptions: z.never().optional(),
            }),
            z.object({
              loyaltyPointsOptions: z.never().optional(),
              couponOptions: z
                .intersection(
                  z.object({
                    name: z
                      .string()
                      .describe('Coupon name.')
                      .min(1)
                      .max(50)
                      .optional(),
                    discountType: z
                      .enum([
                        'UNKNOWN',
                        'FIXED_AMOUNT',
                        'PERCENTAGE',
                        'FREE_SHIPPING',
                      ])
                      .describe('Coupon discount type.')
                      .optional(),
                    limitedToOneItem: z
                      .boolean()
                      .describe(
                        'Whether the coupon is limited to one item.\nIf `true` and a customer pays for multiple items, the discount applies to only the lowest priced item.\nCoupons with a bookings `scope.namespace` are always limited to one item.'
                      )
                      .optional()
                      .nullable(),
                    appliesToSubscriptions: z
                      .boolean()
                      .describe(
                        'Whether the coupon applies to subscription products.'
                      )
                      .optional()
                      .nullable(),
                    discountedCycleCount: z
                      .number()
                      .int()
                      .describe(
                        'Specifies the amount of discounted cycles for a subscription item.\n\n- Can only be set when `scope.namespace = pricingPlans`.\n- If `discountedCycleCount` is empty, the coupon applies to all available cycles.\n- `discountedCycleCount` is ignored if `appliesToSubscriptions = true`.\n\nMax: `999`'
                      )
                      .optional()
                      .nullable(),
                  }),
                  z.intersection(
                    z.xor([
                      z.object({
                        fixedAmountOptions: z.never().optional(),
                        percentageOptions: z.never().optional(),
                      }),
                      z.object({
                        percentageOptions: z.never().optional(),
                        fixedAmountOptions: z
                          .object({
                            amount: z
                              .number()
                              .describe(
                                'Amount of the discount as a fixed value.'
                              )
                              .min(0.01)
                              .optional(),
                          })
                          .describe('Options for fixed amount discount.'),
                      }),
                      z.object({
                        fixedAmountOptions: z.never().optional(),
                        percentageOptions: z
                          .object({
                            percentage: z
                              .number()
                              .describe('Percentage of discount.')
                              .min(0)
                              .max(100)
                              .optional(),
                          })
                          .describe('Options for percentage discounts.'),
                      }),
                    ]),
                    z.xor([
                      z.object({
                        minimumSubtotal: z.never().optional(),
                        scope: z.never().optional(),
                      }),
                      z.object({
                        scope: z.never().optional(),
                        minimumSubtotal: z
                          .number()
                          .describe(
                            'Limit the coupon to carts with a subtotal above this number.'
                          ),
                      }),
                      z.object({
                        minimumSubtotal: z.never().optional(),
                        scope: z
                          .object({
                            namespace: z
                              .string()
                              .describe(
                                'Scope namespace (Wix Stores, Wix Bookings, Wix Events, Wix Pricing Plans)'
                              )
                              .optional(),
                            group: z
                              .object({
                                name: z
                                  .string()
                                  .describe('Name of the group.')
                                  .optional(),
                                entityId: z
                                  .string()
                                  .describe('Entity ID of the group.')
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                "Coupon scope's applied group, for example, Event or ticket in Wix Events."
                              )
                              .optional(),
                          })
                          .describe(
                            'Specifies the type of line items this coupon will apply to. See [valid scope values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values).'
                          ),
                      }),
                    ])
                  )
                )
                .describe('Options for coupon reward type.'),
            }),
            z.object({
              couponOptions: z.never().optional(),
              loyaltyPointsOptions: z
                .object({
                  amount: z
                    .number()
                    .int()
                    .describe('Number of loyalty points to give.')
                    .min(1)
                    .max(9999999)
                    .optional(),
                })
                .describe('Options for the Loyalty points reward type.'),
            }),
          ])
        )
        .describe(
          'Reward configuration for the referring customer.\nSpecifies the reward given to an existing customer who referred a new customer to the business.'
        )
        .optional(),
      successfulReferralActions: z
        .array(
          z.enum([
            'UNKNOWN',
            'STORE_ORDER_PLACED',
            'PLAN_ORDERED',
            'TICKET_ORDERED',
            'SESSION_BOOKED',
            'RESTAURANT_ORDER_PLACED',
            'ONLINE_PROGRAM_JOINED',
          ])
        )
        .max(100)
        .optional(),
      emails: z
        .object({
          encourageToReferFriends: z
            .array(
              z.enum([
                'UNKNOWN',
                'STORES',
                'PRICING_PLANS',
                'EVENTS',
                'BOOKINGS',
                'RESTAURANTS',
                'ONLINE_PROGRAMS',
              ])
            )
            .optional(),
          notifyCustomersAboutReward: z
            .boolean()
            .describe(
              'Whether to send email notifications to referring customers when they receive a referral reward.\nIf true, referring customers will be notified by email when their referred friend completes a qualifying action (for example, placing an order).'
            )
            .optional(),
        })
        .describe('Configures email notifications for the referral program.')
        .optional(),
      premiumFeatures: z
        .object({
          referralProgram: z
            .boolean()
            .describe(
              'Whether the site owner has access to the referral program feature.'
            )
            .optional(),
        })
        .describe(
          'Indicates which premium features are available for the current account.'
        )
        .optional(),
    })
    .describe('Activated referral program.')
    .optional(),
});
export const PauseReferralProgramRequest = z.object({});
export const PauseReferralProgramResponse = z.object({
  referralProgram: z
    .object({
      name: z
        .string()
        .describe('Referral program name.')
        .min(2)
        .max(50)
        .optional()
        .nullable(),
      status: z.enum(['UNKNOWN', 'DRAFT', 'ACTIVE', 'PAUSED']).optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the program is updated.\nTo prevent conflicting changes, the current `revision` must be passed when updating the program.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the program was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the program was last updated.')
        .optional()
        .nullable(),
      referredFriendReward: z
        .intersection(
          z.object({
            type: z
              .enum(['UNKNOWN', 'COUPON', 'LOYALTY_POINTS', 'NOTHING'])
              .describe('Type of the reward.')
              .optional(),
          }),
          z.xor([
            z.object({
              couponOptions: z.never().optional(),
              loyaltyPointsOptions: z.never().optional(),
            }),
            z.object({
              loyaltyPointsOptions: z.never().optional(),
              couponOptions: z
                .intersection(
                  z.object({
                    name: z
                      .string()
                      .describe('Coupon name.')
                      .min(1)
                      .max(50)
                      .optional(),
                    discountType: z
                      .enum([
                        'UNKNOWN',
                        'FIXED_AMOUNT',
                        'PERCENTAGE',
                        'FREE_SHIPPING',
                      ])
                      .describe('Coupon discount type.')
                      .optional(),
                    limitedToOneItem: z
                      .boolean()
                      .describe(
                        'Whether the coupon is limited to one item.\nIf `true` and a customer pays for multiple items, the discount applies to only the lowest priced item.\nCoupons with a bookings `scope.namespace` are always limited to one item.'
                      )
                      .optional()
                      .nullable(),
                    appliesToSubscriptions: z
                      .boolean()
                      .describe(
                        'Whether the coupon applies to subscription products.'
                      )
                      .optional()
                      .nullable(),
                    discountedCycleCount: z
                      .number()
                      .int()
                      .describe(
                        'Specifies the amount of discounted cycles for a subscription item.\n\n- Can only be set when `scope.namespace = pricingPlans`.\n- If `discountedCycleCount` is empty, the coupon applies to all available cycles.\n- `discountedCycleCount` is ignored if `appliesToSubscriptions = true`.\n\nMax: `999`'
                      )
                      .optional()
                      .nullable(),
                  }),
                  z.intersection(
                    z.xor([
                      z.object({
                        fixedAmountOptions: z.never().optional(),
                        percentageOptions: z.never().optional(),
                      }),
                      z.object({
                        percentageOptions: z.never().optional(),
                        fixedAmountOptions: z
                          .object({
                            amount: z
                              .number()
                              .describe(
                                'Amount of the discount as a fixed value.'
                              )
                              .min(0.01)
                              .optional(),
                          })
                          .describe('Options for fixed amount discount.'),
                      }),
                      z.object({
                        fixedAmountOptions: z.never().optional(),
                        percentageOptions: z
                          .object({
                            percentage: z
                              .number()
                              .describe('Percentage of discount.')
                              .min(0)
                              .max(100)
                              .optional(),
                          })
                          .describe('Options for percentage discounts.'),
                      }),
                    ]),
                    z.xor([
                      z.object({
                        minimumSubtotal: z.never().optional(),
                        scope: z.never().optional(),
                      }),
                      z.object({
                        scope: z.never().optional(),
                        minimumSubtotal: z
                          .number()
                          .describe(
                            'Limit the coupon to carts with a subtotal above this number.'
                          ),
                      }),
                      z.object({
                        minimumSubtotal: z.never().optional(),
                        scope: z
                          .object({
                            namespace: z
                              .string()
                              .describe(
                                'Scope namespace (Wix Stores, Wix Bookings, Wix Events, Wix Pricing Plans)'
                              )
                              .optional(),
                            group: z
                              .object({
                                name: z
                                  .string()
                                  .describe('Name of the group.')
                                  .optional(),
                                entityId: z
                                  .string()
                                  .describe('Entity ID of the group.')
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                "Coupon scope's applied group, for example, Event or ticket in Wix Events."
                              )
                              .optional(),
                          })
                          .describe(
                            'Specifies the type of line items this coupon will apply to. See [valid scope values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values).'
                          ),
                      }),
                    ])
                  )
                )
                .describe('Options for coupon reward type.'),
            }),
            z.object({
              couponOptions: z.never().optional(),
              loyaltyPointsOptions: z
                .object({
                  amount: z
                    .number()
                    .int()
                    .describe('Number of loyalty points to give.')
                    .min(1)
                    .max(9999999)
                    .optional(),
                })
                .describe('Options for the Loyalty points reward type.'),
            }),
          ])
        )
        .describe(
          'Reward configuration for the referred friend.\nSpecifies the reward given to a new customer who was referred to the business.'
        )
        .optional(),
      referringCustomerReward: z
        .intersection(
          z.object({
            type: z
              .enum(['UNKNOWN', 'COUPON', 'LOYALTY_POINTS', 'NOTHING'])
              .describe('Type of the reward.')
              .optional(),
          }),
          z.xor([
            z.object({
              couponOptions: z.never().optional(),
              loyaltyPointsOptions: z.never().optional(),
            }),
            z.object({
              loyaltyPointsOptions: z.never().optional(),
              couponOptions: z
                .intersection(
                  z.object({
                    name: z
                      .string()
                      .describe('Coupon name.')
                      .min(1)
                      .max(50)
                      .optional(),
                    discountType: z
                      .enum([
                        'UNKNOWN',
                        'FIXED_AMOUNT',
                        'PERCENTAGE',
                        'FREE_SHIPPING',
                      ])
                      .describe('Coupon discount type.')
                      .optional(),
                    limitedToOneItem: z
                      .boolean()
                      .describe(
                        'Whether the coupon is limited to one item.\nIf `true` and a customer pays for multiple items, the discount applies to only the lowest priced item.\nCoupons with a bookings `scope.namespace` are always limited to one item.'
                      )
                      .optional()
                      .nullable(),
                    appliesToSubscriptions: z
                      .boolean()
                      .describe(
                        'Whether the coupon applies to subscription products.'
                      )
                      .optional()
                      .nullable(),
                    discountedCycleCount: z
                      .number()
                      .int()
                      .describe(
                        'Specifies the amount of discounted cycles for a subscription item.\n\n- Can only be set when `scope.namespace = pricingPlans`.\n- If `discountedCycleCount` is empty, the coupon applies to all available cycles.\n- `discountedCycleCount` is ignored if `appliesToSubscriptions = true`.\n\nMax: `999`'
                      )
                      .optional()
                      .nullable(),
                  }),
                  z.intersection(
                    z.xor([
                      z.object({
                        fixedAmountOptions: z.never().optional(),
                        percentageOptions: z.never().optional(),
                      }),
                      z.object({
                        percentageOptions: z.never().optional(),
                        fixedAmountOptions: z
                          .object({
                            amount: z
                              .number()
                              .describe(
                                'Amount of the discount as a fixed value.'
                              )
                              .min(0.01)
                              .optional(),
                          })
                          .describe('Options for fixed amount discount.'),
                      }),
                      z.object({
                        fixedAmountOptions: z.never().optional(),
                        percentageOptions: z
                          .object({
                            percentage: z
                              .number()
                              .describe('Percentage of discount.')
                              .min(0)
                              .max(100)
                              .optional(),
                          })
                          .describe('Options for percentage discounts.'),
                      }),
                    ]),
                    z.xor([
                      z.object({
                        minimumSubtotal: z.never().optional(),
                        scope: z.never().optional(),
                      }),
                      z.object({
                        scope: z.never().optional(),
                        minimumSubtotal: z
                          .number()
                          .describe(
                            'Limit the coupon to carts with a subtotal above this number.'
                          ),
                      }),
                      z.object({
                        minimumSubtotal: z.never().optional(),
                        scope: z
                          .object({
                            namespace: z
                              .string()
                              .describe(
                                'Scope namespace (Wix Stores, Wix Bookings, Wix Events, Wix Pricing Plans)'
                              )
                              .optional(),
                            group: z
                              .object({
                                name: z
                                  .string()
                                  .describe('Name of the group.')
                                  .optional(),
                                entityId: z
                                  .string()
                                  .describe('Entity ID of the group.')
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                "Coupon scope's applied group, for example, Event or ticket in Wix Events."
                              )
                              .optional(),
                          })
                          .describe(
                            'Specifies the type of line items this coupon will apply to. See [valid scope values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values).'
                          ),
                      }),
                    ])
                  )
                )
                .describe('Options for coupon reward type.'),
            }),
            z.object({
              couponOptions: z.never().optional(),
              loyaltyPointsOptions: z
                .object({
                  amount: z
                    .number()
                    .int()
                    .describe('Number of loyalty points to give.')
                    .min(1)
                    .max(9999999)
                    .optional(),
                })
                .describe('Options for the Loyalty points reward type.'),
            }),
          ])
        )
        .describe(
          'Reward configuration for the referring customer.\nSpecifies the reward given to an existing customer who referred a new customer to the business.'
        )
        .optional(),
      successfulReferralActions: z
        .array(
          z.enum([
            'UNKNOWN',
            'STORE_ORDER_PLACED',
            'PLAN_ORDERED',
            'TICKET_ORDERED',
            'SESSION_BOOKED',
            'RESTAURANT_ORDER_PLACED',
            'ONLINE_PROGRAM_JOINED',
          ])
        )
        .max(100)
        .optional(),
      emails: z
        .object({
          encourageToReferFriends: z
            .array(
              z.enum([
                'UNKNOWN',
                'STORES',
                'PRICING_PLANS',
                'EVENTS',
                'BOOKINGS',
                'RESTAURANTS',
                'ONLINE_PROGRAMS',
              ])
            )
            .optional(),
          notifyCustomersAboutReward: z
            .boolean()
            .describe(
              'Whether to send email notifications to referring customers when they receive a referral reward.\nIf true, referring customers will be notified by email when their referred friend completes a qualifying action (for example, placing an order).'
            )
            .optional(),
        })
        .describe('Configures email notifications for the referral program.')
        .optional(),
      premiumFeatures: z
        .object({
          referralProgram: z
            .boolean()
            .describe(
              'Whether the site owner has access to the referral program feature.'
            )
            .optional(),
        })
        .describe(
          'Indicates which premium features are available for the current account.'
        )
        .optional(),
    })
    .describe('Paused referral program.')
    .optional(),
});
export const GetAiSocialMediaPostsSuggestionsRequest = z.object({
  options: z
    .object({
      topic: z
        .string()
        .describe(
          'Topic to generate social media post suggestions for. For example, fitness, education, or technology.'
        )
        .max(512)
        .optional(),
    })
    .optional(),
});
export const GetAiSocialMediaPostsSuggestionsResponse = z.object({
  suggestions: z
    .array(
      z.object({
        postContent: z
          .string()
          .describe('Suggested post content.')
          .max(4096)
          .optional(),
        hashtags: z.array(z.string()).max(256).optional(),
      })
    )
    .max(3)
    .optional(),
  referFriendsPageUrl: z
    .string()
    .describe('Referral URL to refer friends.')
    .max(2083)
    .optional()
    .nullable(),
});
export const GenerateAiSocialMediaPostsSuggestionsRequest = z.object({
  options: z
    .object({
      topic: z
        .string()
        .describe(
          'Topic to generate social media post suggestions for. For example, fitness, education, or technology.'
        )
        .max(512)
        .optional(),
    })
    .optional(),
});
export const GenerateAiSocialMediaPostsSuggestionsResponse = z.object({
  suggestions: z
    .array(
      z.object({
        postContent: z
          .string()
          .describe('Suggested post content.')
          .max(4096)
          .optional(),
        hashtags: z.array(z.string()).max(256).optional(),
      })
    )
    .max(3)
    .optional(),
  referFriendsPageUrl: z
    .string()
    .describe('Referral URL to refer friends.')
    .max(2083)
    .optional()
    .nullable(),
});
export const GetReferralProgramPremiumFeaturesRequest = z.object({});
export const GetReferralProgramPremiumFeaturesResponse = z.object({
  referralProgram: z
    .boolean()
    .describe('Whether the site has the referral program feature enabled.')
    .optional(),
});
